import { createContext, FC, PropsWithChildren } from 'react';

// project import
import config from '../config';

// types
import { CustomizationProps, FontFamily, I18n, MenuOrientation, PresetColor, ThemeDirection, ThemeMode } from '../types/config';
import { atomWithStorage } from 'jotai/utils';
import { useAtom } from 'jotai';

// initial state
const initialState: CustomizationProps = {
	...config,
	onChangeContainer: () => {},
	onChangeLocalization: (lang: I18n) => {},
	onChangeMode: (mode: ThemeMode) => {},
	onChangePresetColor: (theme: PresetColor) => {},
	onChangeDirection: (direction: ThemeDirection) => {},
	onChangeMiniDrawer: (miniDrawer: boolean) => {},
	onChangeMenuOrientation: (menuOrientation: MenuOrientation) => {},
	onChangeFontFamily: (fontFamily: FontFamily) => {},
};

// ==============================|| CONFIG CONTEXT & PROVIDER ||============================== //

const ConfigContext = createContext(initialState);

const configAtom = atomWithStorage<CustomizationProps>('config', initialState);

const ConfigProvider: FC<PropsWithChildren> = ({ children }) => {
	const [config, setConfig] = useAtom(configAtom);

	const onChangeContainer = () => {
		setConfig({
			...config,
			container: !config.container,
		});
	};

	const onChangeLocalization = (lang: I18n) => {
		setConfig({
			...config,
			i18n: lang,
		});
	};

	const onChangeMode = (mode: ThemeMode) => {
		setConfig({
			...config,
			mode,
		});
	};

	const onChangePresetColor = (theme: PresetColor) => {
		setConfig({
			...config,
			presetColor: theme,
		});
	};

	const onChangeDirection = (direction: ThemeDirection) => {
		setConfig({
			...config,
			themeDirection: direction,
		});
	};

	const onChangeMiniDrawer = (miniDrawer: boolean) => {
		setConfig({
			...config,
			miniDrawer,
		});
	};

	const onChangeMenuOrientation = (layout: MenuOrientation) => {
		setConfig({
			...config,
			menuOrientation: layout,
		});
	};

	const onChangeFontFamily = (fontFamily: FontFamily) => {
		setConfig({
			...config,
			fontFamily,
		});
	};

	return (
		<ConfigContext.Provider
			value={{
				...config,
				onChangeContainer,
				onChangeLocalization,
				onChangeMode,
				onChangePresetColor,
				onChangeDirection,
				onChangeMiniDrawer,
				onChangeMenuOrientation,
				onChangeFontFamily,
			}}
		>
			{children}
		</ConfigContext.Provider>
	);
};

// eslint-disable-next-line import/no-unused-modules
export { ConfigProvider, ConfigContext };
